import React from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, 
  Target, 
  Users, 
  Globe, 
  Lock, 
  Zap,
  Heart,
  Award,
  TrendingUp,
  Eye,
  CheckCircle,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './AboutUs.css';

const AboutUs = () => {
  const stats = [
    { value: '500K+', label: 'Active Users', icon: Users },
    { value: '$2B+', label: 'Assets Secured', icon: Shield },
    { value: '50+', label: 'Chains Supported', icon: Globe },
    { value: '99.9%', label: 'Uptime', icon: Zap },
  ];

  const values = [
    {
      icon: Lock,
      title: 'Security First',
      description: 'Your security is our top priority. We implement military-grade encryption and never have access to your private keys.',
      color: '#22c55e'
    },
    {
      icon: Eye,
      title: 'Transparency',
      description: 'We believe in complete transparency. No hidden fees, no data selling, and clear communication about how we operate.',
      color: '#3b82f6'
    },
    {
      icon: Users,
      title: 'User Empowerment',
      description: 'We build tools that give you complete control over your digital assets. Your keys, your crypto, your rules.',
      color: '#8b5cf6'
    },
    {
      icon: Zap,
      title: 'Innovation',
      description: 'We constantly push the boundaries of what\'s possible in Web3, bringing you cutting-edge features and technology.',
      color: '#f59e0b'
    },
    {
      icon: Heart,
      title: 'Community Driven',
      description: 'Our community shapes our roadmap. We listen, learn, and build features that matter most to our users.',
      color: '#ec4899'
    },
    {
      icon: Globe,
      title: 'Accessibility',
      description: 'Crypto should be accessible to everyone. We design intuitive experiences that make Web3 simple for all.',
      color: '#06b6d4'
    }
  ];

  const milestones = [
    {
      year: '2022',
      title: 'The Beginning',
      description: 'Web3 Ledger was founded with a mission to make self-custody simple and secure for everyone.',
      icon: Sparkles
    },
    {
      year: '2023',
      title: 'Rapid Growth',
      description: 'Reached 100,000 users and expanded support to 25+ blockchain networks.',
      icon: TrendingUp
    },
    {
      year: '2024',
      title: 'Major Milestones',
      description: 'Launched staking, trading features, and crossed $1B in secured assets.',
      icon: Award
    },
    {
      year: '2025',
      title: 'Global Expansion',
      description: 'Expanded to 150+ countries with multi-language support and 50+ chain integrations.',
      icon: Globe
    },
    {
      year: '2026',
      title: 'The Future',
      description: 'Continuing to innovate with advanced DeFi tools and institutional-grade features.',
      icon: Zap
    }
  ];

  const features = [
    'Non-custodial wallet - you own your keys',
    'Multi-chain support for 50+ networks',
    'Bank-grade security protocols',
    'Real-time portfolio tracking',
    'Seamless DeFi integration',
    '24/7 customer support'
  ];

  return (
    <div className="about-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="about-hero">
        <div className="hero-glow"></div>
        <div className="hero-grid-pattern"></div>
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
        <div className="container">
          <motion.div 
            className="about-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.span 
              className="hero-badge"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Shield size={14} />
              About Web3 Ledger
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Building the Future of <br />
              <span className="text-gradient">Digital Finance</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              We're on a mission to make cryptocurrency accessible, secure, and simple for everyone. 
              Web3 Ledger empowers you to take full control of your digital assets.
            </motion.p>
            <motion.div 
              className="hero-cta"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Link to="/get-started" className="btn btn-primary">
                Start Your Journey
                <ArrowRight size={18} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="about-stats">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="stat-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <div className="stat-icon">
                  <stat.icon size={20} />
                </div>
                <span className="stat-value">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="about-mission">
        <div className="container">
          <div className="mission-grid">
            <motion.div 
              className="mission-content"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-badge">
                <Target size={14} />
                Our Mission
              </span>
              <h2>Empowering Financial Freedom Through Decentralization</h2>
              <p>
                At Web3 Ledger, we believe that everyone deserves access to secure, 
                private, and truly decentralized financial tools. Traditional financial 
                systems have left billions underserved – we're here to change that.
              </p>
              <p>
                Our platform gives you complete ownership of your digital assets. 
                No third parties, no data sharing, no account freezes. Just you and 
                your crypto, secured by the most advanced technology available.
              </p>
              <div className="mission-features">
                {features.map((feature, index) => (
                  <motion.div 
                    key={index} 
                    className="feature-item"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <CheckCircle size={18} />
                    <span>{feature}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            <motion.div 
              className="mission-visual"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="visual-card">
                <div className="card-glow"></div>
                <div className="visual-icon">
                  <Shield size={48} />
                </div>
                <h3>Your Keys, Your Crypto</h3>
                <p>
                  We never have access to your private keys or recovery phrases. 
                  Your assets are always under your complete control.
                </p>
                <div className="visual-stats">
                  <div className="visual-stat">
                    <Award size={20} />
                    <span>Industry Leading Security</span>
                  </div>
                  <div className="visual-stat">
                    <TrendingUp size={20} />
                    <span>Trusted by 500K+ Users</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="about-values">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-badge">
              <Heart size={14} />
              Our Values
            </span>
            <h2>What We Stand For</h2>
            <p>These core values guide everything we do at Web3 Ledger</p>
          </motion.div>
          
          <div className="values-grid">
            {values.map((value, index) => (
              <motion.div
                key={index}
                className="value-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
              >
                <div className="value-icon" style={{ '--icon-color': value.color }}>
                  <value.icon size={24} />
                </div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
                <div className="card-shine"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="about-timeline">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-badge">
              <TrendingUp size={14} />
              Our Journey
            </span>
            <h2>From Vision to Reality</h2>
            <p>Key milestones in our journey to revolutionize digital finance</p>
          </motion.div>
          
          <div className="timeline">
            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
              >
                <div className="timeline-content">
                  <div className="timeline-icon">
                    <milestone.icon size={18} />
                  </div>
                  <span className="timeline-year">{milestone.year}</span>
                  <h3>{milestone.title}</h3>
                  <p>{milestone.description}</p>
                </div>
                <div className="timeline-dot">
                  <div className="dot-pulse"></div>
                </div>
              </motion.div>
            ))}
            <div className="timeline-line"></div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="container">
          <motion.div 
            className="cta-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="cta-glow"></div>
            <Sparkles className="cta-sparkle" size={32} />
            <h2>Ready to Join Us?</h2>
            <p>
              Experience the future of digital asset management. 
              Start your journey with Web3 Ledger today.
            </p>
            <div className="cta-actions">
              <Link to="/get-started" className="btn btn-primary">
                Get Started
                <ArrowRight size={18} />
              </Link>
              <Link to="/contact" className="btn btn-secondary">
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;
